'use client';

import React, { useState, useEffect } from 'react';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { Tooltip } from '@osmosis/tailwind-ui/components/tooltip/Tooltip';
import { useColors } from '@osmosis/tailwind-ui/helpers/useColors';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { useIntl } from 'react-intl';
import { useLearnAccess } from '../../../../context/LearnAccessContext';
import AssessmentRowItem from './AssessmentRowItem';
import useModal from '../../../../hooks/useModal';
import { extractQuestionSets } from '../../../../lib/quiz/questions';
import { QueueItemType } from '../../../../context/QueueProvider';
import { useQueueContext } from '../../../../context/QueueProvider';
import { useUserContext } from '../../../../context/UserContext';
import { learnQuizRoute } from '../../../../lib/quiz/route';
import { usePagesContext } from '../../../../context/PagesProvider';
import { useLanguageContext } from '../../../../context/LanguageProvider';
import { QuestionSet } from '../../../../types/questions';
import { LanguageCode } from '@osmosis/shared/languages';
import { useCMEContext } from '../../../../context/CMEContext';
import { useQuery } from 'react-query';
import { useFetch } from '../../../../context/FetchProvider';
import { useAssessmentProgress } from '../../../../context/AssessmentProgressProvider';
import { LearnDataVideo } from '@osmosis/shared/types/learn';
import { QuestionSection } from '@osmosis/shared/types/learndb/learnData';
import { getItems } from '../../../../lib/frontend/quiz';
import { processQuestions } from './utils';
import { LockoutModalVariant } from '../../../../constants/lockout';

import dynamic from 'next/dynamic';

const FlashcardBrowserModal = dynamic(
  () => import('../../../quiz/FlashcardBrowserModal'),
  { ssr: false },
);

const LockoutModalContent = dynamic(
  () => import('../../../LockoutModalContent'),
  { ssr: false },
);

const Modal = dynamic(() => import('../../../Modal'), { ssr: false });

type Props = {
  flashcards?: LearnDataVideo['sections']['flashcards'];
  questions?: LearnDataVideo['sections']['questions'];
};

/**
 * AssessmentsBlockClient.
 */
export default function AssessmentsBlockClient(props: Props) {
  const { params: { concept, lang: language = LanguageCode.EN } = {}, push } =
    usePagesContext();
  const colors = useColors();
  const intl = useIntl();
  const { get, post } = useFetch();
  const [loginPopup, setLoginPopup] = useState<boolean>(false);
  const { activeLanguage } = useLanguageContext();

  const browseFlashcardsModal = useModal();
  const { cmeModal, cme } = useCMEContext();

  const { saveToQueue } = useQueueContext();

  const { handleQuestionsAccess, handleFlashcardAccess } = useLearnAccess();
  const { user, hasPrime } = useUserContext();
  const { assessmentProgress } = useAssessmentProgress();

  const [processedQuestions, setProcessedQuestions] = useState<
    QuestionSection | undefined
  >();

  const { flashcards, questions: initQuestions } = props;

  const isActiveUser = !!user && hasPrime;

  useEffect(() => {
    const setQuestions = async () => {
      if (!initQuestions) return;

      const getHandler = (list: number[]) => getItems(post, list);
      const newQuestions = await processQuestions(
        isActiveUser,
        getHandler,
        initQuestions,
        concept,
      );

      if (Array.isArray(newQuestions)) {
        const processedQuestions = (newQuestions as QuestionSection[]).reduce(
          (acc: QuestionSection, curr: QuestionSection) => ({
            ...acc,
            ...curr,
          }),
          {},
        );

        setProcessedQuestions(processedQuestions);
      } else {
        setProcessedQuestions(newQuestions);
      }
    };

    setQuestions();
  }, [initQuestions, isActiveUser, concept, get]);

  const { data: overriddenDisplayNames } = useQuery(
    'overriddenDisplayNames',
    async () => {
      const { data } = await get<{ [path: string]: string }>(
        `/api/translations/${language || 'en'}/products/${
          user?.productID || 0
        }`,
      );

      return data;
    },
  );

  const sets = extractQuestionSets(
    processedQuestions,
    activeLanguage,
    overriddenDisplayNames,
    assessmentProgress?.questions,
  );

  const cmeIndex = sets.findIndex((set) => set.key === 'cme');
  if (cmeIndex > -1) {
    sets.splice(cmeIndex, 1);
  }

  /**
   * Previews flashcards.
   */
  const handlePreviewFlashcards = async () => {
    if (!user) {
      setLoginPopup(true);
      return;
    }
    handleFlashcardAccess(() => browseFlashcardsModal.show());
  };

  /**
   * Returns pageName.
   */
  const getPageName = (concept?: string | string[]) => {
    let pageName = '';

    if (concept) {
      if (Array.isArray(concept)) {
        pageName = encodeURIComponent(
          concept.map((c) => decodeURIComponent(c)).join('/'),
        );
      } else {
        pageName = concept;
      }
    }

    return pageName;
  };

  /**
   * Starts quiz.
   */
  const handleStartQuiz = async ({
    quizTag,
    quizType,
    isResumeClicked,
  }: {
    quizTag: string;
    quizType: string;
    isResumeClicked?: boolean;
  }) => {
    void push(
      learnQuizRoute({
        pageName: getPageName(concept),
        quizTag,
        quizType,
        resume: isResumeClicked,
      }),
    );
  };

  /**
   * Adds items.
   */
  const handleAddItems = async (nodeIDs: number[], type: QueueItemType) => {
    await saveToQueue(nodeIDs, type);
  };

  const handleStartQuestions = (quizTag: string, isResumeClicked: boolean) => {
    handleQuestionsAccess(
      () =>
        handleStartQuiz({
          quizType: 'questions',
          quizTag,
          isResumeClicked: isResumeClicked,
        }),
      null,
      !user,
    );
  };

  const handleStartFlashcards = (isResumeClicked: boolean) => {
    if (!user) {
      setLoginPopup(true);
      return;
    }
    handleFlashcardAccess(() =>
      handleStartQuiz({
        quizType: 'flashcards',
        quizTag: 'fc',
        isResumeClicked: isResumeClicked,
      }),
    );
  };

  const handleAddQuestionItems = (nodeIDs: number[]) => {
    handleQuestionsAccess(
      async () => await handleAddItems(nodeIDs, 'questions'),
    );
  };

  const handleAddFlashcardItems = () => {
    if (flashcards?.list) {
      handleQuestionsAccess(
        async () => await handleAddItems(flashcards?.list, 'flashcards'),
      );
    }
  };

  const translateQbankName = ({
    set,
    language,
  }: {
    set: QuestionSet;
    language: LanguageCode;
  }) => {
    if (language === LanguageCode.EN) return set.overrideName || set.name;

    return set.localizedName || set.overrideName || set.name;
  };

  const handleAccessToStartQuiz = async (
    quizTag: string,
    quizUrl: string | undefined,
    isResumeClicked: boolean,
  ) => {
    if (user) {
      return handleStartQuestions(quizTag, isResumeClicked);
    }

    if (!quizUrl) {
      setLoginPopup(true);
    } else {
      handleStartQuestions(quizTag, isResumeClicked);
    }
  };

  const handleCloseModal = () => {
    setLoginPopup(false);
  };
  return (
    <>
      {!!flashcards?.list?.length && (
        <>
          <Box>
            <AssessmentRowItem
              name={intl.formatMessage({
                defaultMessage: 'Flashcards',
                id: 'learn.assessments.flashcards',
              })}
              onPreview={handlePreviewFlashcards}
              onAdd={handleAddFlashcardItems}
              onStart={handleStartFlashcards}
              total={
                assessmentProgress?.flashcards.available ||
                flashcards.list.length
              }
              complete={assessmentProgress?.flashcards.attempted || 0}
              languageTag={true}
            />
          </Box>
          {browseFlashcardsModal.isVisible && (
            <FlashcardBrowserModal
              nodeIDs={flashcards.list}
              modalProps={browseFlashcardsModal}
            />
          )}
        </>
      )}

      {sets.map((set) => (
        <Box key={set.key}>
          <AssessmentRowItem
            quizUrl={set.quizUrl}
            name={translateQbankName({ language: activeLanguage, set })}
            onStart={(isResumeClicked) =>
              handleAccessToStartQuiz(set.key, set.quizUrl, isResumeClicked)
            }
            onAdd={() => handleAddQuestionItems(set.list)}
            total={set.progress?.available || set.list.length}
            complete={set.progress?.attempted || 0}
          />
        </Box>
      ))}

      {cme?.enabled && (
        <AssessmentRowItem
          name={intl.formatMessage({
            defaultMessage: 'CME Credits',
            id: 'learn.assessments.cme',
          })}
          total={cme.total}
          complete={cme.attempted}
          onStart={cmeModal.toggle}
          customHeaderActions={
            cme.attempted / cme.total === 1 ? (
              <>
                <Icon
                  size="lg"
                  icon={faCheck}
                  color={colors.positive}
                  style={{
                    outline: 'none',
                  }}
                  data-tooltip-id="complete-cme-check"
                  data-tooltip-content="CME credits complete"
                  className={``}
                />

                <Tooltip
                  id="complete-cme-check"
                  style={{ padding: 8, fontSize: 14 }}
                />
              </>
            ) : undefined
          }
        />
      )}

      {loginPopup && (
        <Modal
          role="dialog"
          isVisible={loginPopup}
          hide={() => false}
          overlayOpacity={0.2}
          _className="!overflow-visible !max-h-[85vh] !max-w-[600px] !w-full"
        >
          <LockoutModalContent
            dataTestId="quiz-lockout-modal"
            onClose={handleCloseModal}
            closeButton={true}
            variant={LockoutModalVariant.QUIZ}
          />
        </Modal>
      )}
    </>
  );
}
